import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';

const SnackbarContext = createContext();

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export default function SnackbarProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const [snackbarKey, setSnackbarKey] = useState(0);

  const showSnackbar = useCallback((msg, level = 'success') => {
    setMessage(msg);
    setSeverity(level);
    setSnackbarKey(prev => prev + 1); // Force re-render even if already open
    setOpen(true);
  }, []);

  const handleClose = useCallback((_, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        key={snackbarKey}
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: 24, sm: 24 },
          zIndex: theme => theme.zIndex.snackbar,
          '& .MuiAlert-root': {
            minWidth: { xs: '90%', sm: 'auto' },
            maxWidth: { xs: '90%', sm: 600 },
            boxShadow: 3,
          },
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
